import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowRight, ShieldCheck } from "lucide-react";
import axios from "axios";
import { money, Price } from "@/pricing";
import { RequestTyreCta } from "@/RequestTyre";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SizePicker({ variants, sku, onPick }) {
  return (
    <div className="size-picker" role="radiogroup" aria-label="Tyre size" data-testid="size-picker">
      {variants.map((v) => (
        <button
          key={v.sku}
          type="button"
          role="radio"
          aria-checked={v.sku === sku}
          disabled={v.stock < 1}
          className={v.sku === sku ? "size-chip active" : "size-chip"}
          onClick={() => onPick(v.sku)}
          data-testid={`size-option-${v.sku}`}
        >
          <b>{v.size}</b>
          <span>{v.load_speed || "\u00a0"}</span>
          <small>{v.stock < 1 ? "Out of stock" : money(v.price_usd)}</small>
        </button>
      ))}
    </div>
  );
}

export default function ProductPage({ add }) {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [missing, setMissing] = useState(false);
  const [params] = useSearchParams();
  const [sku, setSku] = useState(params.get("sku"));
  const [hint, setHint] = useState("");
  const [added, setAdded] = useState(false);
  const nav = useNavigate();
  useEffect(() => {
    axios.get(`${API}/products/${id}`).then((r) => setP(r.data)).catch(() => setMissing(true));
  }, [id]);
  if (missing)
    return (
      <main className="empty-state">
        <h1>This tyre is no longer listed.</h1>
        <RequestTyreCta testId="missing-product-request" />
      </main>
    );
  if (!p) return <div className="loading">Loading product...</div>;
  const v = p.variants.find((x) => x.sku === sku && x.stock > 0);
  const buy = (go) => {
    if (!v) return setHint("Please select a size first.");
    add(p, v);
    go ? nav("/cart") : setAdded(true);
  };
  const specs = Object.entries(p.specs).filter(([k]) => k !== "Size" && k !== "Load / Speed");
  return (
    <main className="detail-page">
      <Link to="/catalog/tires" className="back-link" data-testid="product-back-link">
        ← Back to tyres
      </Link>
      <div className="detail-layout">
        <div className="detail-image">
          <img src={p.image} alt={`${p.brand} ${p.name}`} />
          <span className="image-stamp">TTC / TYRE</span>
        </div>
        <div className="detail-copy">
          <p className="eyebrow red">{p.brand} / tyre</p>
          <h1>{p.name}</h1>
          <p className="detail-description">{p.description}</p>
          <div className="detail-price">
            {v ? <Price usd={v.price_usd} testId="product-price" /> : <Price usd={p.price_usd} from={p.price_max_usd > p.price_usd} testId="product-price" />}
            <span>USD / per tyre{v ? ` · ${v.size}` : ""}</span>
          </div>
          <p className="field-label">SELECT SIZE</p>
          <SizePicker variants={p.variants} sku={sku} onPick={(s) => { setSku(s); setHint(""); setAdded(false); }} />
          {hint && <p className="form-error" data-testid="size-required-error">{hint}</p>}
          <div className="spec-list">
            {v && (
              <div>
                <span>SKU</span>
                <b data-testid="selected-sku">{v.sku}</b>
              </div>
            )}
            {v?.load_speed && (
              <div>
                <span>Load / Speed</span>
                <b>{v.load_speed}</b>
              </div>
            )}
            {specs.map(([k, val]) => (
              <div key={k}>
                <span>{k}</span>
                <b>{val}</b>
              </div>
            ))}
          </div>
          <p className="stock-line" data-testid="stock-line">
            <span className="stock-dot" /> {v ? `${v.stock} in stock · ships in 2–4 business days` : "Select a size to see availability"}
          </p>
          <div className="purchase-actions">
            <button className={v ? "btn btn-red" : "btn btn-red is-unselected"} onClick={() => buy(true)} data-testid="buy-now-button">
              Buy now <ArrowRight size={17} />
            </button>
            <button className={v ? "btn btn-outline" : "btn btn-outline is-unselected"} onClick={() => buy(false)} data-testid="add-to-cart-button">
              {added ? "Added to cart" : "Add to cart"}
            </button>
          </div>
          <p className="fitment-note">
            <ShieldCheck size={16} /> Always verify fitment with your vehicle specialist before purchase.
          </p>
          <RequestTyreCta brandModel={`${p.brand} ${p.name}`} productId={p.id} testId="product-request-tyre" />
        </div>
      </div>
    </main>
  );
}
